import React, { useState, useEffect, useRef } from 'react';
import { Modal, Title, Tabs, Button, Group, Alert, Box, LoadingOverlay } from '@mantine/core';
import {
  IconFileExport,
  IconInfoCircle,
  IconEdit,
  IconFileText,
  IconFileTypePdf
} from '@tabler/icons-react';
import RichTextEditor from '../../RichTextEditor';
import {
  prepareLIAReportData,
  generateLIAReportTemplate,
  exportLIAReportToWord,
  exportLIAReportToPDF,
  LIAReportData
} from '../../Templates/liaReportTemplate';
import { assessmentScheduleService } from 'services/lia/assessmentScheduleService';

/* eslint-disable */
interface LIAReportModalProps {
  opened: boolean;
  onClose: () => void;
  assessment: any;
  selectedCategory?: string;
  onSaveContent?: (content: string) => void;
}
/* eslint-enable */

const LIAReportModal: React.FC<LIAReportModalProps> = ({
  opened,
  onClose,
  assessment,
  selectedCategory,
  onSaveContent
}) => {
  /* eslint-disable */
  const [activeTab, setActiveTab] = useState<string | null>('editor');
  const [reportData, setReportData] = useState<LIAReportData | null>(null);
  const [reportHtml, setReportHtml] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [editorContent, setEditorContent] = useState<string>('');
  const [fetchedData, setFetchedData] = useState<any>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  /* eslint-enable */

  useEffect(() => {
    if (opened && assessment) {
      generateReport();
    }
  }, [opened, assessment, selectedCategory]);

  const generateReport = async () => {
    if (!assessment) return;

    setIsGenerating(true);
    try {
      const assessmentScheduleId = assessment.msAssessmentScheduleID || assessment.id;

      // Fetch export data (required) and summary report (optional)
      const exportData = await assessmentScheduleService.exportAssessmentData(
        assessmentScheduleId,
        selectedCategory
      );

      let summaryDescription = '';
      try {
        const summaryReportData =
          await assessmentScheduleService.getSummaryReport(assessmentScheduleId);
        summaryDescription = summaryReportData?.data?.[0]?.summaryDescription || '';
      } catch {
        // Summary report is optional - ignore errors
      }

      const jsonText = await exportData.text();
      const parsedData = JSON.parse(jsonText);
      setFetchedData(parsedData);

      const dataToUse = parsedData.data || parsedData;

      // Pass empty string if no summary available - template handles optional summary
      const liaData = prepareLIAReportData(dataToUse, summaryDescription || undefined);
      setReportData(liaData);

      const html = generateLIAReportTemplate(liaData);
      setReportHtml(html);
      setEditorContent(html);

      if (onSaveContent) {
        onSaveContent(html);
      }
    } catch {
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportWord = async (content: string = editorContent) => {
    if (!assessment) return;

    const categoryPrefix = selectedCategory ? `${selectedCategory}_` : '';
    const fileName = `LIA_${categoryPrefix}${assessment.name?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.docx`;
    await exportLIAReportToWord(content, fileName);
  };

  const handleExportPDF = (content: string = editorContent) => {
    if (!assessment) return;

    const categoryPrefix = selectedCategory ? `${selectedCategory}_` : '';
    const fileName = `LIA_${categoryPrefix}${assessment.name?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
    exportLIAReportToPDF(content, fileName);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={4}>Learning Impact Assessment Report</Title>}
      size='95%'
      styles={{
        header: {
          backgroundColor: '#f8f9fa',
          padding: '16px 24px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px'
        },
        body: { padding: '0' }
      }}
    >
      <LoadingOverlay visible={isGenerating} overlayProps={{ blur: 2 }} />

      <div style={{ position: 'relative', minHeight: '500px' }}>
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value='editor' leftSection={<IconEdit size={14} />}>
              Edit Report
            </Tabs.Tab>
            <Tabs.Tab value='preview' leftSection={<IconFileText size={14} />}>
              Preview
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value='editor' p='md'>
            {/* Rich Text Editor */}
            <Alert
              icon={<IconInfoCircle size={16} />}
              title='LIA Report Editor'
              color='blue'
              variant='light'
              mb='md'
            >
              Edit the Learning Impact Assessment report below. The report includes HTML-based
              charts that will export properly to Word format.
              {selectedCategory && (
                <span>
                  {' '}
                  Filtered by category: <strong>{selectedCategory}</strong>
                </span>
              )}
              You can format text, add tables, and modify chart sections. The report follows the LIA
              2025 template format with embedded visualizations.
            </Alert>

            <RichTextEditor initialContent={editorContent} contentRef={editorRef} height={650} />
          </Tabs.Panel>

          <Tabs.Panel value='preview' p='md'>
            <Box
              p='md'
              style={{
                border: '1px solid #eee',
                borderRadius: '4px',
                minHeight: '600px',
                maxHeight: '700px',
                overflowY: 'auto'
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: editorContent }} />
            </Box>

            <Group justify='flex-end' mt='md' gap='sm'>
              <Button
                leftSection={<IconFileTypePdf size={16} />}
                onClick={() => handleExportPDF(editorContent)}
                color='#d32f2f'
                variant='outline'
              >
                Export to PDF
              </Button>
            </Group>
          </Tabs.Panel>
        </Tabs>
      </div>

      <Box p='md' style={{ backgroundColor: '#f8f9fa' }}>
        <Group justify='space-between'>
          <Button variant='subtle' onClick={onClose}>
            Close
          </Button>
          <Group gap='sm'>
            <Button
              leftSection={<IconFileTypePdf size={16} />}
              onClick={() => handleExportPDF(editorContent)}
              color='#d32f2f'
              variant='outline'
            >
              Export to PDF
            </Button>
            <Button
              leftSection={<IconFileExport size={16} />}
              onClick={() => handleExportWord(editorContent)}
              color='#7B2183'
            >
              Export to Word
            </Button>
          </Group>
        </Group>
      </Box>

      <style>{`
      .chart-display:hover {
        border-color: #7b2183 !important;
        box-shadow: 0 0 0 1px #7b2183;
      }

      .data-table-container {
        max-height: 300px;
        overflow-y: auto;
        border: 1px solid #eee;
        border-radius: 4px;
      }

      .data-table {
        width: 100%;
        border-collapse: collapse;
      }

      .data-table th {
        background-color: #f5f5f5;
        padding: 10px;
        text-align: left;
        border: 1px solid #ddd;
        position: sticky;
        top: 0;
        z-index: 10;
      }

      .data-table td {
        padding: 8px 10px;
        border: 1px solid #ddd;
      }

      .data-table tr:nth-child(even) {
        background-color: #f9f9f9;
      }
      
      /* Ensure all tables have visible borders */
      table {
        border-collapse: collapse;
        border: 1px solid #000;
        width: 100%;
        margin-bottom: 10px;
      }
      
      table th,
      table td {
        border: 1px solid #000;
        padding: 8px;
      }
      
      table th {
        background-color: #f5f5f5;
        font-weight: bold;
      }
    `}</style>
    </Modal>
  );
};

export default LIAReportModal;
